import React, { useCallback, useState } from 'react';
import { LogoLink } from './components/Logo';
import { TopologyViz } from './components/TopologyViz';
import './App.css';

const SITE_URL = 'https://tryaegis.io';
const SUPPORT_EMAIL = 'support@tryaegis.io';
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;
const CONTACT_FORM_URL =
  process.env.REACT_APP_CONTACT_FORM_URL ?? 'https://forms.gle/kKzRG4vAJ5yTJ2JCA';
const INSTALL_CMD_LINUX =
  process.env.REACT_APP_INSTALL_CMD_LINUX ?? 'curl -fsSL https://getaegis.sh | sh';
const INSTALL_CMD_WINDOWS =
  process.env.REACT_APP_INSTALL_CMD_WINDOWS ??
  'iwr https://getaegis.sh -UseBasicParsing | iex';

type InstallTarget = 'linux' | 'windows';

const App: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [installTarget, setInstallTarget] = useState<InstallTarget>('linux');

  /** Default <a href="mailto:"> is sometimes swallowed (e.g. iframe previews); assign at top window. */
  const openSupportEmail = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    try {
      (window.top ?? window).location.assign(SUPPORT_MAILTO);
    } catch {
      window.location.assign(SUPPORT_MAILTO);
    }
  }, []);

  const copyInstall = useCallback(async () => {
    const activeCommand = installTarget === 'windows' ? INSTALL_CMD_WINDOWS : INSTALL_CMD_LINUX;
    try {
      await navigator.clipboard.writeText(activeCommand);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [installTarget]);

  return (
    <div className="landing">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className="landing__nav" role="banner">
        <div className="landing__nav-inner">
          <LogoLink variant="nav" />
          <nav aria-label="Primary">
            <ul className="landing__nav-links">
              <li>
                <a href="#product">Product</a>
              </li>
              <li>
                <a href="#topology">Topology</a>
              </li>
              <li>
                <a href="#install">Install</a>
              </li>
              <li>
                <a href="#how-it-works">How it works</a>
              </li>
              <li>
                <a href="#faq">FAQ</a>
              </li>
              <li>
                <a href="#early-adopters">Early access</a>
              </li>
              <li>
                <a href={SUPPORT_MAILTO} onClick={openSupportEmail}>
                  Support
                </a>
              </li>
              <li>
                <a href={CONTACT_FORM_URL} target="_blank" rel="noopener noreferrer">
                  Contact form
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      <main id="main" className="landing__main">
        <section className="hero" aria-labelledby="hero-title">
          <p className="hero__eyebrow">EDR for AI workloads</p>
          <h1 id="hero-title" className="hero__title hero__title--stacked">
            <span className="hero__title-line">Prevent wasted GPU time</span>
            <span className="hero__title-line hero__title-line--emphasis">before jobs fail</span>
          </h1>
          <p className="hero__lead">
            Aegis AI is an endpoint-style detection and response layer for ML infrastructure.
            It surfaces host and accelerator instability early, so teams avoid stalls, crashes,
            and silent corruption on long-running training and inference.
          </p>
          <p className="hero__note">
            Vendor-agnostic. Linux and Windows. Runs on Kubernetes, bare-metal hosts, and VMs, wherever your ML workloads live.
          </p>
          <div className="hero__actions">
            <a className="btn btn--primary" href="#install">
              Install Aegis
            </a>
            <a className="btn btn--ghost" href="#product">
              Why teams use it
            </a>
          </div>
          <div className="install-command" id="install">
            <div>
              <div className="install-command__label">Quick install</div>
              <div className="install-command__tabs" role="tablist" aria-label="Install target">
                <button
                  type="button"
                  role="tab"
                  aria-selected={installTarget === 'linux'}
                  className={`install-command__tab${installTarget === 'linux' ? ' install-command__tab--active' : ''}`}
                  onClick={() => {
                    setInstallTarget('linux');
                    setCopied(false);
                  }}
                >
                  Linux/macOS
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={installTarget === 'windows'}
                  className={`install-command__tab${installTarget === 'windows' ? ' install-command__tab--active' : ''}`}
                  onClick={() => {
                    setInstallTarget('windows');
                    setCopied(false);
                  }}
                >
                  Windows PowerShell
                </button>
              </div>
              <div className="install-command__text">
                {installTarget === 'windows' ? INSTALL_CMD_WINDOWS : INSTALL_CMD_LINUX}
              </div>
            </div>
            <button
              type="button"
              className="btn btn--copy"
              onClick={copyInstall}
              data-copied={copied}
              aria-label="Copy install command"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="install-hint">
            The bootstrap script routes to shell or PowerShell output depending on your environment. Use Linux/macOS for
            POSIX shells and the Windows tab for native PowerShell.
          </p>
        </section>

        <section
          className="section section--tight-top"
          id="product"
          aria-labelledby="product-title"
        >
          <h2 id="product-title" className="section__title">
            Reliability decisions from low-level infrastructure signals
          </h2>
          <p className="section__subtitle section__subtitle--wide">
            Aegis extends observability into decisions you can enforce: clear reliability
            states from noisy hardware telemetry, so you keep jobs on healthy hosts instead
            of discovering failure after wasted GPU hours.
          </p>
          <div className="feature-grid">
            <article className="feature-card">
              <h3 className="feature-card__title">Detect</h3>
              <p className="feature-card__body">
                Continuously capture instability signals across GPU, host, and interconnect
                layers: not only utilization, but whether the stack is safe to run on.
              </p>
            </article>
            <article className="feature-card">
              <h3 className="feature-card__title">Correlate</h3>
              <p className="feature-card__body">
                Map issues across GPU, PCIe, CPU, memory, and topology context so root
                causes are visible across the hardware paths that actually matter.
              </p>
            </article>
            <article className="feature-card">
              <h3 className="feature-card__title">Score</h3>
              <p className="feature-card__body">
                Produce high-confidence health signals your team can act on quickly, before
                retries land on the same bad hosts.
              </p>
            </article>
            <article className="feature-card">
              <h3 className="feature-card__title">Enforce</h3>
              <p className="feature-card__body">
                Feed automation and scheduling with reliability-aware signals: gate risky
                hosts, route retries safely, and bridge observability to action.
              </p>
            </article>
          </div>
        </section>

        <section className="section section--topology" id="topology" aria-labelledby="topology-title">
          <h2 id="topology-title" className="section__title">
            Topology-aware metrics, not flat counters
          </h2>
          <p className="section__subtitle section__subtitle--wide">
            In Aegis, topology is treated as contextual state: PCI addresses, NUMA locality, and how
            accelerators attach to the PCIe tree. That matters because the same raw metric can mean
            something different depending on where it was measured: bridge depth, socket affinity,
            and GPU-to-GPU fabric all change what &quot;healthy&quot; looks like.
          </p>
          <div className="topology-copy">
            <div className="topology-copy__col">
              <h3 className="topology-copy__heading">Why it matters for ML reliability</h3>
              <ul className="topology-copy__list">
                <li>
                  Dashboards often chart utilization and error counts without tying them to failure
                  domains. A PCIe or memory hotspot on one path can look like a benign blip when averaged
                  with the rest of the node.
                </li>
                <li>
                  When metrics are scoped to stable device identity and NUMA context, you can spot
                  cross-socket penalties, risky retries, and degraded hosts before jobs burn GPU hours.
                </li>
                <li>
                  Topology gives schedulers and automation something actionable: avoid pairing workloads
                  with NICs or GPUs on the wrong side of the machine, and reason about peer links, not just
                  individual accelerators in isolation.
                </li>
              </ul>
            </div>
            <TopologyViz />
          </div>
        </section>

        <section className="section" id="how-it-works" aria-labelledby="how-title">
          <h2 id="how-title" className="section__title">
            How Aegis runs on your infrastructure
          </h2>
          <p className="section__subtitle section__subtitle--wide">
            The agent runs alongside workloads on each host, whether that is a Kubernetes
            node, a bare-metal server, or a VM on Linux or Windows. It derives reliability-oriented
            signals from the kernel and hardware and exports to your existing pipeline, so you
            strengthen ML infrastructure reliability without a separate vendor agent per
            accelerator family.
          </p>
          <div className="steps">
            <div className="step">
              <div className="step__num" aria-hidden>
                1
              </div>
              <div>
                <h3 className="step__title">Deploy the agent</h3>
                <p className="step__body">
                  On Kubernetes, use the install flow to prepare namespaces and apply your
                  DaemonSet manifest. On bare metal or VMs, install the host agent for your OS
                  (Linux or Windows) using your standard package or image process. Configure export
                  to your OpenTelemetry collector when you centralize metrics.
                </p>
              </div>
            </div>
            <div className="step">
              <div className="step__num" aria-hidden>
                2
              </div>
              <div>
                <h3 className="step__title">Build the reliability signal</h3>
                <p className="step__body">
                  The agent collects continuous, labeled signals for accelerators, host
                  pressure, PCIe and fabric health, and failure precursors, grounded in how your
                  nodes are actually wired.
                </p>
              </div>
            </div>
            <div className="step">
              <div className="step__num" aria-hidden>
                3
              </div>
              <div>
                <h3 className="step__title">Act before jobs pay the price</h3>
                <p className="step__body">
                  Use consistent health and context in dashboards, runbooks, and automation
                  to reduce wasted GPU time and protect training runs from degrading
                  hardware, not just react after the fact.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="faq" aria-labelledby="faq-title">
          <h2 id="faq-title" className="section__title">
            Frequently asked questions
          </h2>
          <div className="faq">
            <div className="faq__item">
              <h3 className="faq__question">What is Aegis AI?</h3>
              <p className="faq__answer">
                Aegis AI is reliability software for ML infrastructure. It runs as an agent on
                hosts and detects accelerator and host instability early on Kubernetes, bare-metal
                machines, and VMs. Linux and Windows are
                supported. It helps teams prevent wasted GPU time and failed jobs before hard
                failures and silent corruption show up in metrics alone.
              </p>
            </div>
            <div className="faq__item">
              <h3 className="faq__question">How is this different from a GPU utilization dashboard?</h3>
              <p className="faq__answer">
                Dashboards often show utilization and availability, not whether a host is
                safe to run on. Aegis is built for reliability decisions: correlating GPU,
                PCIe, CPU, memory, and topology signals so you can score health and avoid
                reusing risky hosts when jobs retry.
              </p>
            </div>
            <div className="faq__item">
              <h3 className="faq__question">Does Aegis lock us to one GPU vendor?</h3>
              <p className="faq__answer">
                No. It is vendor-agnostic by design across GPU vendors, and across deployment
                targets: Kubernetes, bare metal, and VMs on Linux and Windows. Signals do not
                depend on a single vendor exporter as your only source of truth.
              </p>
            </div>
          </div>
        </section>

        <section
          className="section section--early-adopters"
          id="early-adopters"
          aria-labelledby="early-adopters-title"
        >
          <h2 id="early-adopters-title" className="section__title">
            Want to be an early adopter?
          </h2>
          <p className="section__subtitle section__subtitle--wide">
            We are working with teams that run serious training and inference on Linux and Windows. If you would like
            priority access, product previews, and a direct line to the builders, use the contact form and tell us how
            you deploy ML workloads today.
          </p>
          <p className="early-adopters__actions">
            <a
              className="btn btn--primary"
              href={CONTACT_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open contact form
            </a>
          </p>
        </section>
      </main>

      <footer className="landing__footer">
        <div className="landing__footer-inner">
          <div>
            <div className="landing__footer-brand">
              <LogoLink variant="footer" />
            </div>
            <p className="landing__footer-meta">
              EDR-style reliability for ML infrastructure. Linux, Windows, Kubernetes, bare metal, and VMs.
            </p>
          </div>
          <ul className="landing__footer-links">
            <li>
              <a href={SITE_URL}>Home</a>
            </li>
            <li>
              <a href="https://getaegis.sh/">Install script</a>
            </li>
            <li>
              <a href="#faq">FAQ</a>
            </li>
            <li>
              <a href="#early-adopters">Early access</a>
            </li>
            <li>
              <a
                href={SUPPORT_MAILTO}
                onClick={openSupportEmail}
                className="landing__footer-email"
              >
                {SUPPORT_EMAIL}
              </a>
            </li>
            <li>
              <a href={CONTACT_FORM_URL} target="_blank" rel="noopener noreferrer">
                Contact form
              </a>
            </li>
          </ul>
        </div>
      </footer>
    </div>
  );
};

export default App;
